import { Link, useRouterState } from "@tanstack/react-router";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocale } from "@/hooks/useLocale";
import { ensureDict } from "@/lib/i18n-dict";
import { LOCALES, LOCALE_LABELS, LOCALE_NAMES, localePath, DEFAULT_LOCALE } from "@/lib/i18n";

/** Strip the locale prefix from the current pathname so we can re-prefix it. */
function basePath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (first && (LOCALES as readonly string[]).includes(first)) {
    segments.shift();
  }
  return segments.length ? `/${segments.join("/")}` : "/";
}

export function LanguageSwitcher() {
  const current = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const base = basePath(pathname);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
        <Globe className="size-4" aria-hidden />
        <span className="font-medium">{LOCALE_LABELS[current]}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-60 overflow-hidden rounded-md border border-border bg-popover p-0 shadow-[0_20px_50px_-12px_var(--shadow-color,rgba(0,0,0,0.45))] duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-top-1"
      >
        {LOCALES.map((locale) => {
          const href = localePath(locale, base);
          const isActive = locale === current;
          return (
            <DropdownMenuItem
              key={locale}
              asChild
              className="group rounded-none border-b border-border/50 p-0 last:border-b-0 focus:bg-foreground/[0.03]"
            >
              <Link
                to={href as never}
                onPointerEnter={() => void ensureDict(locale)}
                onFocus={() => void ensureDict(locale)}
                hrefLang={locale === DEFAULT_LOCALE ? "en" : locale}
                lang={locale}
                dir="ltr"
                aria-current={isActive ? "page" : undefined}
                className="flex min-h-14 w-full items-center gap-4 px-4 py-2.5 text-popover-foreground transition-colors duration-150 hover:bg-foreground/[0.03]"
              >
                <span
                  className={`w-8 shrink-0 font-mono text-[0.68rem] leading-none font-medium tracking-wider uppercase transition-colors duration-150 ${
                    isActive
                      ? "text-primary-ink"
                      : "text-muted-foreground/60 group-hover:text-muted-foreground"
                  }`}
                  aria-hidden
                >
                  {LOCALE_LABELS[locale]}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    dir={locale === "ar" ? "rtl" : "ltr"}
                    className={`language-menu-type block text-[0.94rem] leading-tight tracking-tight transition-colors duration-150 ${
                      isActive
                        ? "font-medium text-foreground"
                        : "text-foreground/70 group-hover:text-foreground"
                    }`}
                  >
                    {LOCALE_NAMES[locale]}
                  </span>
                </span>
                {isActive && (
                  <span
                    className="ms-auto size-1.5 shrink-0 rounded-full bg-primary shadow-[0_0_8px_var(--primary)]"
                    aria-hidden
                  />
                )}
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
